import { css, keyframes, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import type { SetRequired } from 'type-fest';

import { createClassNames } from '@hrworks/design-system/functions';
import { mq, overflowEllipsis, shouldForwardProp } from '@hrworks/design-system/utilities';
import { HelpText as _HelpText, type HelpTextProps } from '@hrworks/sui-shared/components';
import { legacyFontSizeBySizeMap } from '@hrworks/sui-shared/functions';
import type { Size } from '@hrworks/sui-shared/types';

import { ButtonStyles } from '../Button';
import type { InputFieldProps } from './InputField.types';

const componentConfig = (theme: Theme) =>
  ({
    buttonFontSize: '0.875em',
    inputOutlineWidth: 1,
    floatingLabelFontSize: '0.857em',
    inputFontSize: '1em',
    offsetX: {
      extraSmall: 12,
      small: 14,
      medium: 16,
      large: 18,
      extraLarge: 20,
    },
    offsetY: {
      extraSmall: 8,
      small: 10,
      medium: 12,
      large: 14,
      extraLarge: 16,
    },
    inputLineHeight: theme.eclipseTier2Typography.bodyMd.lineHeight,
  }) as const;

export const generateInputBoxShadowStyles = ({ color, width = 1 }: { color: string; width?: number }) =>
  `0px 0px 0px ${width}px inset ${color}`;

const Container = styled.div<{ size: Size }>(({ theme, size }) => ({
  position: 'relative',
  fontSize: legacyFontSizeBySizeMap[size],
  [mq.conditionalTransition]: {
    transitionProperty: 'all',
    transitionDuration: theme.variables.animationDuration.normal,
  },
}));

const InputContainer = styled.div({
  position: 'relative',
});

const floatingLabelStyles = (
  theme: Theme,
  size: Required<InputFieldProps>['size'],
  labelPlacement: InputFieldProps['labelPlacement'],
) => {
  const { offsetY, floatingLabelFontSize, inputOutlineWidth, inputFontSize, inputLineHeight } = componentConfig(theme);

  // Solves a bug for the onClick on the ComboBox where it wouldnt trigger the onclick.
  const enablePointerEvents = keyframes({
    100: {
      pointerEvents: 'all',
    },
  });

  const labelOffset = `${floatingLabelFontSize} / 2`;
  const halfLeading = `(${inputLineHeight} * ${inputFontSize} - ${inputFontSize}) / 2`;
  const insideLabelTop = `calc(${offsetY[size] / 2}px + ${labelOffset} + ${inputOutlineWidth}px + ${halfLeading})`;

  return css({
    ...theme.eclipseTier2Typography.bodySm,
    fontSize: floatingLabelFontSize,
    top: 0,
    cursor: 'default',
    animation: `${enablePointerEvents} ${theme.variables.animationDuration.normal}`,

    ...(labelPlacement === 'inside' && {
      top: insideLabelTop,
    }),
  });
};

type LabelProps = {
  isLabelFloating: boolean;
  size: Size;
  endSlotsWidth?: string;
} & SetRequired<
  Pick<InputFieldProps, 'hasInputFocus' | 'disabled' | 'validationState' | 'labelPlacement' | 'verticalLabelAlignment'>,
  'labelPlacement'
>;

const Label = styled.label<LabelProps>(
  ({
    theme,
    isLabelFloating,
    verticalLabelAlignment,
    size,
    endSlotsWidth,
    hasInputFocus,
    disabled,
    validationState,
    labelPlacement,
  }) => {
    const { offsetX, offsetY } = componentConfig(theme);

    return [
      {
        [mq.conditionalTransition]: {
          transitionProperty: 'top, max-width, transform',
          transitionDuration: theme.variables.animationDuration.normal,
        },
        '*': {
          transitionProperty: 'font-size, color',
          transitionDuration: theme.variables.animationDuration.normal,
        },
        display: 'inline-flex',
        position: 'absolute',
        alignItems: 'center',
        gap: '0.375em',
        cursor: 'text',
        fontSize: '1em',
        padding: '0 0.666em',
        zIndex: 2,
        pointerEvents: 'none',
        top: '50%',
        transform: 'translateY(-50%)',
        left: `calc(${offsetX[size]}px - 0.666em)`,
        maxWidth: `calc(100% - 2 * 0.666em)`,
        borderRadius: theme.eclipseTier1.radius['8px'],
        color: theme.eclipseTier2Color.text.subtlest,

        // Only the resting label is aligned; once floating, labelPlacement owns the position.
        ...(verticalLabelAlignment === 'top' &&
          !isLabelFloating && {
            top: offsetY[size],
            transform: 'none',
          }),
        backgroundColor: theme.eclipseTier2Color.background.input,

        ...(hasInputFocus && {
          color: theme.eclipseTier2Color.text.selected,
        }),

        ...(validationState && {
          color: theme.eclipseTier2Color.text[validationState === 'danger' ? 'error' : validationState].default,
        }),

        ...(disabled && {
          color: theme.eclipseTier2Color.text.disabled,
          backgroundColor: theme.eclipseTier2Color.background.disabled.subtle,
        }),

        ...(endSlotsWidth &&
          (!isLabelFloating || labelPlacement === 'inside') && {
            maxWidth: `calc(100% - ${endSlotsWidth})`,
          }),
      },
      isLabelFloating && floatingLabelStyles(theme, size, labelPlacement),
    ];
  },
);

const LabelText = styled.div(overflowEllipsis);

const HelpText = styled(_HelpText, {
  shouldForwardProp,
})<SetRequired<HelpTextProps, 'size'>>(({ theme, size }) => {
  const { offsetX } = componentConfig(theme);

  return {
    paddingLeft: offsetX[size],
    paddingRight: offsetX[size],
  };
});

const Footer = styled.div({
  display: 'flex',
});

const Counter = styled(HelpText)({
  flexShrink: 0,
  marginLeft: 'auto',
  fontVariantNumeric: 'tabular-nums',
});

const inputStyles = (
  theme: Theme,
  validationState: InputFieldProps['validationState'],
  size: Size,
  labelPlacement: Required<InputFieldProps>['labelPlacement'],
  endSlotsWidth?: string,
  disabled?: boolean,
) => {
  const { offsetX, offsetY, inputLineHeight, inputFontSize, inputOutlineWidth, floatingLabelFontSize } =
    componentConfig(theme);

  const textHeight = `${inputLineHeight} * ${inputFontSize}`;

  return css({
    display: 'block',
    width: '100%',
    color: theme.eclipseTier2Color.text.default,
    border: 0,
    borderRadius: theme.eclipseTier1.radius['8px'],
    outline: `${inputOutlineWidth}px solid transparent`,
    backgroundColor: theme.eclipseTier2Color.background.input,
    padding: `${offsetY[size]}px ${offsetX[size]}px`,
    boxShadow: generateInputBoxShadowStyles({ color: theme.eclipseTier2Color.border.input }),
    lineHeight: inputLineHeight,
    fontSize: inputFontSize,

    // Keeps the absolutely positioned label inside the box when a textarea is resized down.
    minHeight: `calc(${2 * offsetY[size]}px + ${textHeight})`,

    ...(labelPlacement === 'inside' && {
      paddingTop: `calc(${offsetY[size]}px + ${floatingLabelFontSize})`,
      borderRadius: offsetY[size],
      minHeight: `calc(${2 * offsetY[size]}px + ${floatingLabelFontSize} + ${textHeight})`,
    }),

    '::placeholder, & ::placeholder': {
      color: theme.eclipseTier2Color.text.subtlest,
      opacity: 1,
    },

    [mq.conditionalTransition]: {
      transitionProperty: 'color, outline, border-color, box-shadow',
      transitionDuration: theme.variables.animationDuration.normal,
    },

    ':focus,:focus-within': {
      boxShadow: generateInputBoxShadowStyles({ color: theme.eclipseTier2Color.border.selected, width: 2 }),
    },

    ...(endSlotsWidth && {
      paddingRight: endSlotsWidth,
    }),

    ...(validationState &&
      (() => {
        const validationColor =
          theme.eclipseTier2Color.border[validationState === 'danger' ? 'error' : validationState];

        return {
          boxShadow: generateInputBoxShadowStyles({
            color: validationColor,
          }),

          ':focus,:focus-within': {
            boxShadow: generateInputBoxShadowStyles({
              color: validationColor,
              width: 2,
            }),
          },
        };
      })()),

    ':-webkit-autofill': {
      [` ~ ${sel.Label}`]: floatingLabelStyles(theme, size, labelPlacement),
    },

    ...(disabled && {
      cursor: 'not-allowed',
      color: theme.eclipseTier2Color.text.disabled,
      backgroundColor: theme.eclipseTier2Color.background.disabled.subtle,
      boxShadow: generateInputBoxShadowStyles({
        color: theme.eclipseTier2Color.border.disabled,
      }),
    }),
  });
};

// Space at the right of the input for the end slots and the dropdown arrow
const endSlotsWidth = (theme: Theme, size: Size, endSlotCount: number, hasDropdownArrow?: boolean) => {
  const { offsetX, buttonFontSize } = componentConfig(theme);

  if (hasDropdownArrow) {
    // Alone, the arrow keeps the space of an end slot; end slots start right at its left edge
    return endSlotCount > 0
      ? `calc(${buttonFontSize} + 1.25 * ${offsetX[size]}px + ${endSlotCount} * (${buttonFontSize} + 1.75 * ${offsetX[size]}px))`
      : `calc(${buttonFontSize} + 2 * ${offsetX[size]}px)`;
  }
  if (endSlotCount > 0) {
    return `calc(${endSlotCount} * (${buttonFontSize} + 2 * ${offsetX[size]}px))`;
  }
};

const endSlotBaseStyles = (theme: Theme, size: Size, disabled?: boolean, index = 0, hasDropdownArrow?: boolean) => {
  const { offsetX, buttonFontSize } = componentConfig(theme);
  const slotsOffset = `${index} * (1em + 1.75 * ${offsetX[size]}px)`;

  return css({
    '&&': { position: 'absolute' },
    right: hasDropdownArrow ? `calc(1em + ${offsetX[size]}px + ${slotsOffset})` : `calc(${slotsOffset})`,
    top: '50%',
    transform: 'translateY(-50%)',
    aspectRatio: '1/1',
    padding: 0,
    marginInline: offsetX[size] / 4,
    zIndex: 3,
    width: `calc(1em + 1.5 * ${offsetX[size]}px)`,
    // fontSize sets the reference for the `1em` unit in the width calc above.
    // Without it, 1em would inherit from the input text size and break the button dimensions.
    fontSize: buttonFontSize,

    ...(disabled && {
      color: theme.eclipseTier2Color.text.disabled,
    }),
  });
};

const endSlotStaticStyles = (theme: Theme, size: Size, disabled?: boolean, index = 0, hasDropdownArrow?: boolean) =>
  css([endSlotBaseStyles(theme, size, disabled, index, hasDropdownArrow)]);

const endSlotInteractiveStyles = (
  theme: Theme,
  size: Size,
  disabled?: boolean,
  index = 0,
  hasDropdownArrow?: boolean,
) =>
  css([
    ButtonStyles.generateButtonStyles({ theme, size, variant: 'subtle', corner: 'pill' }),
    endSlotBaseStyles(theme, size, disabled, index, hasDropdownArrow),
    {
      outlineOffset: -1,
      justifyContent: 'center',
    },
  ]);

type DropdownArrowProps = {
  $size: Size;
  $disabled?: boolean;
  $isOpen: boolean;
  $isTrigger: boolean;
};

// Narrower than an end slot because it has no hover background, its icon sits where the last end slot's icon would.
// A trigger renders it as button.
const DropdownArrow = styled('span', {
  shouldForwardProp: (prop) => prop !== 'as' && shouldForwardProp(prop),
})<DropdownArrowProps>(({ theme, $size, $disabled, $isOpen, $isTrigger }) => {
  const { offsetX, buttonFontSize } = componentConfig(theme);

  return {
    position: 'absolute',
    top: '50%',
    right: offsetX[$size] * 0.75,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    aspectRatio: '1/1',
    width: `calc(1em + ${offsetX[$size] / 2}px)`,
    fontSize: buttonFontSize,
    color: theme.eclipseTier2Color.text.brand.default,
    pointerEvents: 'none',
    transform: 'translateY(-50%)',
    [mq.conditionalTransition]: {
      transition: `transform ${theme.variables.animationDuration.normal}`,
    },

    ...($isOpen && {
      transform: 'translateY(-50%) rotateX(180deg)',
    }),

    ...($isTrigger && {
      padding: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      pointerEvents: 'auto',
    }),

    ...($disabled && {
      color: theme.eclipseTier2Color.text.disabled,
      cursor: 'default',
    }),
  };
});

const components = { Container, InputContainer, Label, LabelText, HelpText, Footer, Counter, DropdownArrow };
const { cls, sel } = createClassNames('InputField', components);

export const S = {
  componentConfig,
  inputStyles,
  endSlotsWidth,
  endSlotStaticStyles,
  endSlotInteractiveStyles,
  ...components,
  cls,
  sel,
} as const;
